import { useState, useCallback } from "react";
import find from "lodash/find";
import useDefaultCountry from "@/hooks/defaults/useDefaultCountry";
import { useGetStatesQuery } from "@/modules/shared/store/services/endpoints/get-states";

/**
 * useDealSummaryLocation — manages country/state/address interactions for the deal summary step.
 *
 * Initialises countryId and countryIsoCode from the user's default country,
 * handles country change (clears state when country switches), and provides
 * a Google Places callback that auto-selects the matching state.
 *
 * @param {Function} setValue — react-hook-form setValue
 * @param {string}   scope   — form field prefix (e.g. "details")
 */
export default function useDealSummaryLocation(setValue, scope) {
  const defaultCountry = useDefaultCountry();
  const [countryId, setCountryId] = useState(defaultCountry?.countryId ?? null);
  const [countryIsoCode, setCountryIsoCode] = useState(defaultCountry?.countryIsoCode ?? null);
  const defaultCountryId = defaultCountry?.countryId ?? null;
  const { data: states = [] } = useGetStatesQuery(countryId, { skip: !countryId });

  const handleCountryChange = useCallback(
    (item, fieldOnChange) => {
      if (!item) {
        fieldOnChange(undefined);
        setCountryIsoCode(null);
        setCountryId(null);
        setValue(`${scope}.stateId`, null);
        return;
      }
      fieldOnChange(item.value);
      setCountryIsoCode(item.isoCode);
      if (countryId !== item.value) setValue(`${scope}.stateId`, null);
      setCountryId(item.value);
    },
    [countryId, setValue, scope],
  );

  const handlePlaceSelected = useCallback(
    ({ addressLine1, city, state: stateCode, zipCode }) => {
      const fullAddress = [addressLine1, city, stateCode, zipCode].filter(Boolean).join(", ");
      if (fullAddress) setValue(`${scope}.address`, fullAddress, { shouldDirty: true });

      if (!stateCode) return;
      const stateRecord = find(states, ["code", stateCode]);
      if (stateRecord) {
        setValue(`${scope}.stateId`, stateRecord.parameterId, { shouldDirty: true });
      }
    },
    [states, setValue, scope],
  );

  return { countryId, countryIsoCode, defaultCountryId, handleCountryChange, handlePlaceSelected };
}
